import { z } from "zod";

export const ColorScaleSchema = z.object({
  50: z.string().optional(),
  100: z.string().optional(),
  200: z.string().optional(),
  300: z.string().optional(),
  400: z.string().optional(),
  500: z.string(),
  600: z.string().optional(),
  700: z.string().optional(),
  800: z.string().optional(),
  900: z.string().optional(),
  950: z.string().optional(),
});

export type ColorScale = z.infer<typeof ColorScaleSchema>;

export const ColorSchemeColorsSchema = z.object({
  primary: ColorScaleSchema,
  accent: ColorScaleSchema,
  secondary: ColorScaleSchema.optional(),
  success: ColorScaleSchema.optional(),
  error: ColorScaleSchema.optional(),
  warning: ColorScaleSchema.optional(),
  info: ColorScaleSchema.optional(),
  background: z.object({
    primary: z.string(),
    secondary: z.string().optional(),
    tertiary: z.string().optional(),
  }),
  foreground: z
    .object({
      primary: z.string(),
      secondary: z.string().optional(),
      muted: z.string().optional(),
    })
    .optional(),
  surface: z
    .object({
      primary: z.string(),
      elevated: z.string().optional(),
      overlay: z.string().optional(),
    })
    .optional(),
});

export type ColorSchemeColors = z.infer<typeof ColorSchemeColorsSchema>;

export const SpacingConfigSchema = z.object({
  gridUnit: z.union([z.literal(4), z.literal(8)]).default(4),
  scale: z.record(z.string(), z.string()).optional(),
});

export type SpacingConfig = z.infer<typeof SpacingConfigSchema>;

export const TypographyConfigSchema = z.object({
  fontFamily: z.object({
    display: z.array(z.string()),
    body: z.array(z.string()),
    mono: z.array(z.string()).optional(),
  }),
  fontSizes: z
    .object({
      xs: z.string().optional(),
      sm: z.string().optional(),
      base: z.string().optional(),
      lg: z.string().optional(),
      xl: z.string().optional(),
      "2xl": z.string().optional(),
      "3xl": z.string().optional(),
    })
    .optional(),
});

export type TypographyConfig = z.infer<typeof TypographyConfigSchema>;

export const MotionConfigSchema = z.object({
  timingFunction: z.string().default("cubic-bezier(0.4, 0, 0.2, 1)"),
  duration: z
    .object({
      fast: z.string().default("120ms"),
      normal: z.string().default("200ms"),
      slow: z.string().default("400ms"),
    })
    .optional(),
});

export type MotionConfig = z.infer<typeof MotionConfigSchema>;

export const PilotThemeConfigSchema = z.object({
  /** Slug-safe identifier. Used in CSS selector [data-theme="<name>"] and localStorage. */
  name: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  displayName: z.string(),
  colors: z.object({
    dark: ColorSchemeColorsSchema,
    light: ColorSchemeColorsSchema.optional(),
  }),
  spacing: SpacingConfigSchema.optional(),
  typography: TypographyConfigSchema.optional(),
  motion: MotionConfigSchema.optional(),
});

export type PilotThemeConfig = z.infer<typeof PilotThemeConfigSchema>;

export type ColorScheme = "dark" | "light";
